import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const buildProjectPayload = (state, name) => ({
  name: name || state.projectName,
  pages: state.pages.map((p) => ({ id: p.id, name: p.name, width: p.width, height: p.height, objects: p.objects, background: p.background, masterId: p.masterId || 'default' })),
  masters: state.masters,
  scratch: { objects: state.scratch?.objects || [] }, // the shared scratch area (never printed)
  canvas_unit: state.canvasUnit,
  layout_mode: state.layoutMode === 'spread' ? 'spread' : 'single', // View ▸ Two-Page Spread is a property of the publication (booklets)
  spread_style: state.spreadStyle === 'joined' ? 'joined' : 'separate', // … shown as separate pages or as one joined sheet with a fold line
  grid_visible: state.gridVisible,
  grid_spacing: state.gridSpacing,
  guides: state.guides,
  scheme_id: state.schemeId,
  custom_schemes: state.customSchemes || [],
  font_scheme: state.fontScheme || undefined,
  shared_from: state.sharedFrom || undefined, // provenance of a copy that came through a share link
});

export const listProjects = async () => (await axios.get(`${API}/projects`)).data;
/** id · name · folder_id only (no pages) — enough to colour-code document tabs by folder. */
export const listProjectIndex = async () => (await axios.get(`${API}/projects/index`)).data;
export const getProject = async (id) => (await axios.get(`${API}/projects/${id}`)).data;
export const deleteProject = async (id) => (await axios.delete(`${API}/projects/${id}`)).data;

// Sharing (File ▸ Share): an opt-in, unguessable token per publication; recipients open /app?share=<token> and get a copy.
export const getShare = async (id) => (await axios.get(`${API}/projects/${id}/share`)).data; // { project_id, shared, share_token }
export const enableShare = async (id) => (await axios.post(`${API}/projects/${id}/share`)).data;
export const disableShare = async (id) => (await axios.delete(`${API}/projects/${id}/share`)).data;
export const getSharedProject = async (token) => (await axios.get(`${API}/shared/${encodeURIComponent(token)}`)).data; // project + shared_by, is_owner
export const shareUrlFor = (token) => `${window.location.origin}/app?share=${encodeURIComponent(token)}`;

// Folders (File ▸ Open): nested (parent_id, null = top level), one folder per publication; deleting a folder moves it, its
// sub-folders and its publications to the Trash together.
export const listFolders = async () => (await axios.get(`${API}/folders`)).data;
export const createFolder = async ({ name, color, parentId }) => (await axios.post(`${API}/folders`, { name, color: color || null, parent_id: parentId || null })).data;
export const updateFolder = async (id, patch) => (await axios.patch(`${API}/folders/${id}`, patch)).data; // { name?, color?, parent_id? ('' = top level) }
export const moveFolder = async (id, parentId) => updateFolder(id, { parent_id: parentId || '' });
export const deleteFolder = async (id) => (await axios.delete(`${API}/folders/${id}`)).data; // { ok, trashed: { folders, publications }, days }
export const moveProject = async (id, folderId) => (await axios.post(`${API}/projects/${id}/move`, { folder_id: folderId || null })).data;
/** Several publications at once: { ids, action: 'move' | 'delete', folderId? } → { affected }. Delete = move to the Trash. */
export const bulkProjects = async ({ ids, action, folderId }) => (await axios.post(`${API}/projects/bulk`, { ids, action, folder_id: folderId || null })).data;
/** A fresh publication with the same content ("Copy of X"), same folder, not shared. */
export const duplicateProject = async (id) => (await axios.post(`${API}/projects/${id}/duplicate`)).data;

// Trash (File ▸ Trash): deleted publications / folders wait 30 days before they are purged.
export const listTrash = async () => (await axios.get(`${API}/trash`)).data; // { folders, projects, counts, purge_after_days }
export const restoreFromTrash = async ({ projects = [], folders = [], all = false } = {}) => (await axios.post(`${API}/trash/restore`, { projects, folders, all })).data; // { restored: { folders, publications } }
export const purgeTrash = async ({ projects = [], folders = [], all = false } = {}) => (await axios.post(`${API}/trash/purge`, { projects, folders, all })).data; // { removed }

// Recent activity (File ▸ Home): what changed lately — saved, shared, imported, copied, moved, deleted, restored.
export const listActivity = async (limit = 8) => (await axios.get(`${API}/activity`, { params: { limit } })).data; // { items, trash_count }
export const clearActivity = async () => (await axios.delete(`${API}/activity`)).data;

/** Save (create or update). Returns saved project. */
export const saveProject = async (state, { name, asNew = false, thumbnail } = {}) => {
  const payload = buildProjectPayload(state, name);
  if (thumbnail) payload.thumbnail = thumbnail;
  if (state.projectId && !asNew) {
    return (await axios.put(`${API}/projects/${state.projectId}`, payload)).data;
  }
  const first = state.pages[0];
  const created = (await axios.post(`${API}/projects`, { name: payload.name, page_preset: 'custom', custom_width: first.width, custom_height: first.height })).data;
  return (await axios.put(`${API}/projects/${created.id}`, payload)).data;
};

export const listBuildingBlocks = async (category) => (await axios.get(`${API}/building-blocks`, { params: category ? { category } : {} })).data;
export const createBuildingBlock = async (block) => (await axios.post(`${API}/building-blocks`, block)).data;
export const deleteBuildingBlock = async (id) => (await axios.delete(`${API}/building-blocks/${id}`)).data;

// Templates: favourites (starred ids) and custom templates saved from a publication
export const getTemplateFavorites = async () => (await axios.get(`${API}/templates/favorites`)).data;
export const saveTemplateFavorites = async (ids) => (await axios.put(`${API}/templates/favorites`, { ids })).data;
export const listCustomTemplates = async () => (await axios.get(`${API}/templates/custom`)).data; // summaries (no project)
export const getCustomTemplate = async (id) => (await axios.get(`${API}/templates/custom/${id}`)).data; // full template incl. project
export const createCustomTemplate = async (tpl) => (await axios.post(`${API}/templates/custom`, tpl)).data;
export const updateCustomTemplate = async (id, patch) => (await axios.put(`${API}/templates/custom/${id}`, patch)).data;
export const deleteCustomTemplate = async (id) => (await axios.delete(`${API}/templates/custom/${id}`)).data;

export const getBusinessInfo = async () => (await axios.get(`${API}/business-info`)).data;
export const saveBusinessInfo = async (info) => (await axios.put(`${API}/business-info`, info)).data;

export const searchImages = async (query, source = 'all', page = 1) => (await axios.get(`${API}/images/search`, { params: { query, source, page } })).data;

export const getConverterCapabilities = async () => (await axios.get(`${API}/convert/capabilities`)).data;
/** (Re)start the self-contained LibreOffice runtime install on the server (Import screen ▸ Retry). */
export const installConverterEngine = async () => (await axios.post(`${API}/convert/engine/install`)).data;

/** A bundled sample publication for trying the import flow, as a File (backend/samples/*). */
export const fetchImportSample = async (id = 'missing-fonts') => {
  const res = await axios.get(`${API}/convert/samples/${id}`, { responseType: 'blob' });
  const name = (/filename="([^"]+)"/.exec(res.headers['content-disposition'] || '') || [])[1] || `${id}-sample.odg`;
  return new File([res.data], name, { type: res.data.type || 'application/vnd.oasis.opendocument.graphics' });
};

/** Upload + conversion budget: a 200 MB file on a slow line needs far more than the 3 minutes LibreOffice itself gets. */
const CONVERT_TIMEOUT_MS = 10 * 60 * 1000;
/** Upload-only budget for a conversion *job*: the request ends as soon as the file is stored, the work happens behind a job id. */
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/* ---------- conversion jobs ----------
 * A reverse proxy (Cloudflare in production) ends a request after ~100 s; a big publication takes minutes. So the
 * conversion runs as a job: upload → job id at once → poll → fetch the result / download. See backend/convert_jobs.py. */

/** Start a conversion job. kind: 'import' | 'pdf' | 'png'. Resolves to the job's status document ({ job_id, status, … }). */
export const startConvertJob = async (file, kind = 'import', params = {}, onUploadProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/convert/jobs`, fd, {
    params: { kind, ...params },
    onUploadProgress: (e) => onUploadProgress && e.total && onUploadProgress(Math.round((e.loaded / e.total) * 100)),
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return res.data;
};
export const getConvertJob = async (jobId) => (await axios.get(`${API}/convert/jobs/${jobId}`)).data;
export const getConvertJobResult = async (jobId) => (await axios.get(`${API}/convert/jobs/${jobId}/result`, { timeout: CONVERT_TIMEOUT_MS })).data;
export const downloadConvertJob = async (jobId) => {
  const res = await axios.get(`${API}/convert/jobs/${jobId}/download`, { responseType: 'blob', timeout: CONVERT_TIMEOUT_MS });
  const name = (/filename="([^"]+)"/.exec(res.headers['content-disposition'] || '') || [])[1] || '';
  return { blob: res.data, type: res.headers['content-type'] || '', name };
};
export const cancelConvertJob = async (jobId) => (await axios.delete(`${API}/convert/jobs/${jobId}`)).data;

/**
 * Poll a job until it is done / failed / cancelled. `onStatus(job)` is called with every status document; `signal`
 * (an AbortSignal) stops the polling (the job itself keeps running unless cancelled). Resolves to the final document;
 * rejects with { job } when the job did not succeed.
 */
export const waitForConvertJob = async (jobId, onStatus, { signal, intervalMs = 1500 } = {}) => {
  let misses = 0;
  for (;;) {
    if (signal?.aborted) { const e = new Error('aborted'); e.aborted = true; throw e; }
    let job;
    try {
      job = await getConvertJob(jobId);
      misses = 0;
    } catch (err) {
      // a hiccup on the way (the proxy, a dropped connection) is no reason to give up on a running conversion
      if (err?.response?.status === 404 || err?.response?.status === 401 || misses >= 8) throw err;
      misses += 1;
      await new Promise((r) => setTimeout(r, Math.min(10000, intervalMs * (misses + 1))));
      continue;
    }
    onStatus && onStatus(job);
    if (job.status === 'done') return job;
    if (job.status === 'failed' || job.status === 'cancelled') { const e = new Error(job.error?.message || 'The conversion failed.'); e.job = job; throw e; }
    await new Promise((r) => setTimeout(r, job.status === 'queued' ? intervalMs * 2 : intervalMs));
  }
};

export const importPublisherFile = async (file, onProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/convert/pub/import`, fd, {
    params: { include_previews: true, preview_dpi: 40 },
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    timeout: CONVERT_TIMEOUT_MS,
  });
  return res.data;
};

export const publisherToPdf = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/convert/pub/to-pdf`, fd, { responseType: 'blob', timeout: CONVERT_TIMEOUT_MS });
  return res.data;
};

export const publisherToPng = async (file, dpi = 150) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/convert/pub/to-png`, fd, { params: { dpi }, responseType: 'blob', timeout: CONVERT_TIMEOUT_MS });
  return { blob: res.data, type: res.headers['content-type'] || '' };
};

/** Extract a readable error message from an axios error (handles blob error bodies). */
export const apiErrorMessage = async (err, fallback = 'Something went wrong.') => {
  try {
    let data = err?.response?.data;
    if (data instanceof Blob) data = JSON.parse(await data.text());
    const d = data?.detail;
    if (typeof d === 'string') return d;
    if (d?.message) return d.message;
    if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
    if (err?.job?.error?.message) return err.job.error.message;
    if (err?.code === 'ECONNABORTED') return 'The upload took too long. Check your connection and try again.';
    if (err?.response?.status === 413) return 'The file is larger than this server accepts.';
    if (err?.response?.status === 524 || err?.response?.status === 504) return 'The server took too long to answer. The conversion may still be running — please try again in a moment.';
    if (err?.message && !err?.response) return 'Cannot reach the server. Check your connection and try again.';
  } catch { /* ignore */ }
  return fallback;
};
